import * as React from 'react';
import styled from 'styled-components';
import { rem } from 'polished';
import { __ } from 'react-i18n';
import {
  FlexCol,
  FlexRowCenterVertical,
} from '../../../Defaults/Flexbox/Flexbox';
import { FormWrapper, FormInput } from '../../Form/FormInput';
import {
  prop,
  getCountryFromId,
  getDeliveryInfoFromAddress,
} from '../../../../utilities';
import { Select } from '../../../Zemplin/General/Select';
import { CheckBox } from '../../../Zemplin/General/Checkbox';

interface Props {
  onBlur: any;
  deliveryAddress: any;
  deliveryAddresses: any;
  deliveryAddressesIsFetching: boolean;
  formProblems: any;
  isB2B: boolean;
}

function reducer(state, { field, value }) {
  return { ...state, [field]: value };
}

function DeliveryAddress({
  deliveryAddress,
  onBlur,
  deliveryAddresses,
  deliveryAddressesIsFetching,
  formProblems,
  isB2B,
}: Props) {
  const initialState = getInitialState(deliveryAddress);
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const [deliveryId, setDeliveryId] = React.useState('');
  const { street, street_number, city, zip, country } = state;
  const [addNewAddress, setAddNewAddress] = React.useState(false);

  const { streetProblem, streetNrProblem, cityProblem, zipProblem } = {
    streetProblem: prop(formProblems, 'delivery_address.street'),
    streetNrProblem: prop(formProblems, 'delivery_address.street_number'),
    cityProblem: prop(formProblems, 'delivery_address.city'),
    zipProblem: prop(formProblems, 'delivery_address.zip'),
  };

  const onChange = e => {
    const {
      target: { name: fieldName, value },
    } = e;
    dispatch({ field: fieldName, value });
  };

  const handleBlur = (data: any = null) => {
    const blurData = data && data.street ? data : { ...state };
    onBlur({ ...blurData });
  };

  const handleAddressChange = e => {
    const newId = e.target.value;
    if (newId !== deliveryId) {
      setDeliveryId(newId);
      const info = getDeliveryInfoFromAddress(newId, deliveryAddresses);
      if (info) {
        const data = {
          street: info.delivery_street,
          street_number: info.delivery_number,
          city: info.delivery_city,
          zip: info.delivery_zip,
          country: info.country,
          delivery_address_id: parseInt(newId, 10),
        };

        handleBlur(data);
      }
    }
  };

  const handleNewAddressClick = e => {
    const newValue = e.target.value;
    setAddNewAddress(newValue);
  };

  const remappedAddresses = deliveryAddresses
    ? deliveryAddresses.map(d => ({
        value: d.id,
        name: `${d.delivery_street} ${d.delivery_number}, ${d.delivery_city} ${
          d.delivery_zip
        }, ${getCountryFromId(d.country)}`,
      }))
    : null;

  return (
    <Wrapper>
      {remappedAddresses && remappedAddresses.length > 0 && (
        <PastDeliveryAdrressesWrapper>
          <Select
            placeholder={__('Vyberte zo zoznamu')}
            marginBottom={32}
            name="addresses"
            label={__('Moje adresy')}
            value={deliveryId}
            options={remappedAddresses}
            onChange={handleAddressChange}
          />

          <CheckBox
            name="addNewAddress"
            onClick={handleNewAddressClick}
            selected={addNewAddress}
            label={__('Pridať novú adresu')}
          />
        </PastDeliveryAdrressesWrapper>
      )}

      {(!remappedAddresses ||
        remappedAddresses.length === 0 ||
        addNewAddress) && (
        <>
          <AddressWrapper>
            <FormWrapper noWrap={true} marginRight={32}>
              <FormInput
                name="street"
                type="text"
                label={__('Ulica')}
                value={street}
                maxWidth={100}
                maxWidthPx={!isB2B ? 224 : 320}
                marginRight={!isB2B ? 16 : 0}
                onChange={onChange}
                marginBottom={16}
                onBlur={handleBlur}
                validMessage={streetProblem}
              />
              {!isB2B && (
                <FormInput
                  name="street_number"
                  type="text"
                  label={__('Číslo')}
                  value={street_number}
                  maxWidth={34}
                  maxWidthPx={80}
                  onChange={onChange}
                  marginBottom={16}
                  onBlur={handleBlur}
                  validMessage={streetNrProblem}
                />
              )}
            </FormWrapper>
            <FormWrapper noWrap={true}>
              <FormInput
                name="city"
                type="text"
                label={__('Mesto')}
                value={city}
                maxWidth={100}
                maxWidthPx={224}
                marginRight={16}
                onChange={onChange}
                marginBottom={16}
                onBlur={handleBlur}
                validMessage={cityProblem}
              />
              <FormInput
                name="zip"
                type="text"
                label={__('PSČ')}
                value={zip}
                maxWidth={34}
                maxWidthPx={80}
                onChange={onChange}
                marginBottom={16}
                onBlur={handleBlur}
                validMessage={zipProblem}
              />
            </FormWrapper>{' '}
          </AddressWrapper>

          <Select
            marginBottom={0}
            name="country"
            label={__('Krajina')}
            value={country}
            options={[{ value: 'sk', name: 'Slovensko' }]}
            onChange={onChange}
            onBlur={handleBlur}
          />
        </>
      )}
    </Wrapper>
  );
}

const getInitialState = (deliveryAddress: any) => {
  return {
    street: prop(deliveryAddress, 'street', ''),
    street_number: prop(deliveryAddress, 'street_number', ''),
    city: prop(deliveryAddress, 'city', ''),
    zip: prop(deliveryAddress, 'zip', ''),
    country: prop(deliveryAddress, 'country', 'sk') || 'sk',
  };
};

const Wrapper = styled(FlexCol)``;

const AddressWrapper = styled(FlexRowCenterVertical)`
  flex-flow: row wrap;
`;

const PastDeliveryAdrressesWrapper = styled(FlexCol)`
  margin-bottom: ${rem(32)};
`;

export { DeliveryAddress };
